import React, {Fragment, useEffect, useState} from 'react';
import {compose} from "redux";
import {connect} from "react-redux";
import {translate} from "react-i18next";
import CKEditor from 'ckeditor4-react';
import './style.css';
import {Button, Col, Row, Select} from "antd";
import {DataLanguageConsumer} from "../../contexts/DataLanguage";
import {getEmptyDictionaryForLanguages, getLanguageFlagIconCss, getLanguageLabel} from "../../utils/languages";
import _ from "lodash";
import EnhancedModal from "../enhanced-modal";
import uuidv4 from "uuid";

const $ = window.jQuery;

const mapStateToProps = state => ({
  appLang: state.app.language,
  dataLangs: state.config.dataManagement.dataLanguages
});

const getDefaultConfig = (lang, initialEditorHeight) => ({
  defaultLanguage: 'en',
  language: lang,
  title: false,
  disableNativeSpellChecker: true,
  toolbar: [
    {name: 'styles', items: ['Format', 'Font', 'FontSize']},
    {name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike']},
    {name: 'format', items: ['RemoveFormat']},
    {name: 'colors', items: ['TextColor', 'BGColor']},
    {name: 'insert', items: ['Table']},
    {name: 'links', items: ['Link', 'Unlink']},
    {name: 'paragraph', items: ['NumberedList', 'BulletedList']},
    {name: 'indent', items: ['Outdent', 'Indent']},
    {name: 'justify', items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock']},
    {name: 'clipboard', items: ['Cut', 'Copy', 'Paste']},
    {name: 'source', items: ['Source']}
  ],
  height: initialEditorHeight || 200
});

const MultilanguageHtmlEditor = ({
                                   t,
                                   appLang,
                                   dataLangs,
                                   value: externalValue,
                                   customConfig,
                                   disabled,
                                   disableZoom = false,
                                   initialEditorHeight,
                                   onChange
                                 }) => {

  const [value, setValue] = useState(getEmptyDictionaryForLanguages(dataLangs, ""));
  const [tmpValue, setTmpValue] = useState(null);
  const [language, setLanguage] = useState(null);
  const [editorWrapperUuid] = useState(uuidv4());
  const [isModalVisible, setModalVisibility] = useState(false);
  const [fullScreenEditorInstance, setFullscreenEditorInstance] = useState(null);

  useEffect(() => {
    const oldValue = _.cloneDeep(value);
    setValue(_.merge(oldValue, externalValue));
  }, [externalValue]);

  const callOnChange = value => {
    if (onChange) {
      onChange(value);
    }
  };

  const handleSetLanguage = lang => {
    setLanguage(lang);
    callOnChange(value);
  };

  const setValueForLanguage = (val, lang) => {
    const newValue = {
      ...value,
      [lang]: val
    };
    callOnChange(newValue);
  };

  const handleFullscreenEditorHeight = () => {
    if (fullScreenEditorInstance) {
      fullScreenEditorInstance.resize("100%", $(`#${this.state.editorWrapperUuid}`).outerHeight());
    }
  };

  const handleModalShow = () => {
    setTmpValue(value);
    setModalVisibility(true);
    window.addEventListener("resize", handleFullscreenEditorHeight);
  };

  const handleModalHide = () => {
    setTmpValue(null);
    setModalVisibility(false);
    setFullscreenEditorInstance(null);
    window.removeEventListener("resize", handleFullscreenEditorHeight);
  };

  const handleModalSubmit = () => {
    if (!disabled) {
      callOnChange(tmpValue);
    }
    handleModalHide();
  };

  const setTmpValueForLanguage = (val, lang) => {
    const newTmpValue = {
      ...tmpValue,
      [lang]: val
    };
    setTmpValue(newTmpValue);
  };

  return (
    <DataLanguageConsumer>
      {contextDataLanguage => {
        const lang = contextDataLanguage || language;
        return (
          <Fragment>
            <EnhancedModal
              visible={isModalVisible}
              fullscreen
              withDataLanguageSelector
              footer={
                <Fragment>
                  <Button onClick={handleModalSubmit} icon="zoom-out">
                    {t('commons.buttons.close.title')}
                  </Button>
                </Fragment>
              }
              onCancel={handleModalSubmit}
            >
              <DataLanguageConsumer>
                {modalLanguage => {
                  const lang = modalLanguage || language;
                  return (
                    <div id={editorWrapperUuid} style={{width: "100%", height: "100%"}}>
                      <CKEditor
                        data={tmpValue[lang]}
                        config={(customConfig !== null && customConfig !== undefined)
                          ? {...customConfig, resize_enabled: false}
                          : {...getDefaultConfig(appLang, initialEditorHeight), resize_enabled: false}
                        }
                        onChange={event => setTmpValueForLanguage(event.editor.getData(), lang)}
                        // readOnly={disabled || false}
                        onBeforeLoad={CKEDITOR => {
                          CKEDITOR.disableAutoInline = true; // needed to fix "Error code: editor-element-conflict."
                        }}
                        onInstanceReady={({editor}) => {
                          setFullscreenEditorInstance(editor);
                          editor.resize("100%", $(`#${editorWrapperUuid}`).outerHeight());
                          editor.setReadOnly(disabled);
                        }}
                      />
                    </div>
                  )
                }}
              </DataLanguageConsumer>
            </EnhancedModal>
            {!contextDataLanguage && (
              <Row type="flex" justify="end">
                <Col>
                  <Select
                    value={lang}
                    onChange={handleSetLanguage}
                    style={{width: 100}}
                  >
                    {dataLangs.map(({code}) =>
                      <Select.Option
                        key={code}
                        value={code}
                        title={getLanguageLabel(t, code)}
                      >
                        <i
                          className={`flag-icon ${getLanguageFlagIconCss(code, dataLangs)}`}
                          style={{marginRight: 8}}
                        />
                        {code !== null ? code.toUpperCase() : null}
                      </Select.Option>)
                    }
                  </Select>
                </Col>
              </Row>
            )}
            <CKEditor
              data={value[lang]}
              config={(customConfig !== null && customConfig !== undefined)
                ? customConfig
                : getDefaultConfig(appLang, initialEditorHeight)
              }
              onChange={event => setValueForLanguage(event.editor.getData(), lang)}
              // readOnly={disabled || false}
              onBeforeLoad={CKEDITOR => {
                CKEDITOR.disableAutoInline = true; // needed to fix "Error code: editor-element-conflict."
              }}
              onInstanceReady={({editor}) => {
                editor.setReadOnly(disabled);
              }}
            />
            {!disableZoom && (
              <Row
                type="flex"
                justify="end"
                style={{marginTop: 8}}
              >
                <Col>
                  <Button onClick={handleModalShow} icon="zoom-in">
                    {t('components.htmlEditor.zoomIn.title')}
                  </Button>
                </Col>
              </Row>
            )}
          </Fragment>
        )
      }}
    </DataLanguageConsumer>
  );
}

export default compose(
  translate(),
  connect(mapStateToProps)
)(MultilanguageHtmlEditor);
